import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/common/StatusBadge";
import { toast } from "sonner";

export default function AccountSettings() {
  const { currentAccount } = useAuth();
  const [acct, setAcct] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/account/settings").then((r) => {
      setAcct(r.data);
      setForm({ account_name: r.data.account_name, time_zone: r.data.time_zone,
        primary_contact_name: r.data.primary_contact?.name || "",
        primary_contact_email: r.data.primary_contact?.email || "" });
    }).catch((e) => toast.error(apiError(e)));
  }, [currentAccount]);

  if (!acct) return <PageLoader />;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/account/settings", form);
      setAcct(data); toast.success("Account settings saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Account Settings" description="Manage your client account profile." />
      <div className="max-w-2xl rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span className="font-mono bg-slate-100 rounded px-2 py-0.5">{acct.internal_account_id}</span>
          <StatusBadge status={acct.status} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Account name</Label>
          <Input value={form.account_name || ""} onChange={(e) => setForm({ ...form, account_name: e.target.value })} className="mt-1.5" data-testid="account-name-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Time zone</Label>
          <Input value={form.time_zone || ""} onChange={(e) => setForm({ ...form, time_zone: e.target.value })} className="mt-1.5" data-testid="account-tz-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Primary contact name</Label>
            <Input value={form.primary_contact_name || ""} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Primary contact email</Label>
            <Input value={form.primary_contact_email || ""} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} className="mt-1.5" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-account-settings" className="bg-[#006090] hover:bg-[#004E75]">Save changes</Button>
      </div>
    </div>
  );
}
